import * as Sentry from '@sentry/react';
import debounce from 'lodash/debounce';
import { AppState } from '../../app/store/store';
import {
  TagItem,
  TagBoard,
  TagBoardColumn,
  updateItemsOrder,
  updateItems,
  deleteTagsColumns,
  updateTagsColumnsOrdering,
  deleteItems,
} from '../../api/tagsBoardAPI';
import { selectTagsBoard, actions as tagsBoardActions } from './slice';
import { selectCurrentWorkspaceId } from '../workspacesSlice';

class SyncQueue {
  private columns: { order?: string[]; orderUpdatedAt?: number; toDelete: string[] } = {
    order: undefined,
    orderUpdatedAt: undefined,
    toDelete: [],
  };
  private items: {
    toUpdate: Map<TagItem['id'], TagItem>;
    toDelete: TagItem['id'][];
    order: Map<TagBoardColumn['id'], { order: TagItem[]; updatedAt: number }>;
  } = {
    toUpdate: new Map(),
    toDelete: [],
    order: new Map(),
  };
  get shouldUpdateColumnsOrder() {
    return !!this.columns.order;
  }
  getColumnsOrderQueue() {
    return {
      order: [...this.columns.order],
      updatedAt: this.columns.orderUpdatedAt,
    };
  }
  setColumnsOrderQueue(order: string[], updatedAt: number) {
    this.columns.order = order;
    this.columns.orderUpdatedAt = updatedAt;
  }

  clearColumnsOrderQueue() {
    this.columns.order = undefined;
    this.columns.orderUpdatedAt = undefined;
  }

  get shouldDeleteColumns() {
    return this.columns.toDelete.length > 0;
  }

  getColumnsToDeleteQueue() {
    return [...this.columns.toDelete];
  }

  setColumnsToDeleteQueue(uuids: string[]) {
    this.columns.toDelete = uuids;
  }

  addColumnsToDeleteQueue(uuids: string[]) {
    uuids.forEach((uuid) => {
      if (!this.columns.toDelete.includes(uuid)) {
        this.columns.toDelete.push(uuid);
      }
    });
  }

  clearColumnsToDeleteQueue() {
    this.columns.toDelete = [];
  }

  get shouldDeleteItems() {
    return this.items.toDelete.length > 0;
  }

  getItemsToDeleteQueue() {
    return [...this.items.toDelete];
  }

  addItemsToDeleteQueue(itemsUuids: string[]) {
    itemsUuids.forEach((uuid) => {
      if (!this.items.toDelete.includes(uuid)) {
        this.items.toDelete.push(uuid);
      }
    });
  }

  clearItemsToDeleteQueue() {
    this.items.toDelete = [];
  }

  get shouldUpdateItems() {
    return this.items.toUpdate.size > 0;
  }

  getItemsToUpdateQueue() {
    return Array.from(this.items.toUpdate.values());
  }

  addItemsToUpdateQueue(items: TagItem[]) {
    items.forEach((item: TagItem) => {
      const prevItem = this.items.toUpdate.get(item.id);

      if (!prevItem) {
        this.items.toUpdate.set(item.id, item);
      } else {
        if (prevItem.updatedAt < item.updatedAt) {
          this.items.toUpdate.set(item.id, item);
        }
      }
    });
  }

  clearItemsToUpdateQueue() {
    this.items.toUpdate.clear();
  }

  get shouldUpdateItemsOrder() {
    return this.items.order.size > 0;
  }

  getItemsOrderQueue() {
    return Array.from(this.items.order);
  }

  addItemsOrderQueue(colUuid: string, order: TagItem[], updatedAt: number) {
    const prevOrder = this.items.order.get(colUuid);
    if (!prevOrder) {
      this.items.order.set(colUuid, { order, updatedAt });
    } else {
      if (updatedAt > prevOrder.updatedAt) {
        this.items.order.set(colUuid, { order, updatedAt });
      }
    }
  }

  clearItemsOrderQueue() {
    this.items.order.clear();
  }

  get shouldSync() {
    return (
      this.shouldDeleteColumns ||
      this.shouldUpdateColumnsOrder ||
      this.shouldDeleteItems ||
      this.shouldUpdateItems ||
      this.shouldUpdateItemsOrder
    );
  }

  clearQueue() {
    this.clearColumnsToDeleteQueue();
    this.clearColumnsOrderQueue();
    this.clearItemsToDeleteQueue();
    this.clearItemsToUpdateQueue();
    this.clearItemsOrderQueue();
  }
}
class SyncState {
  syncing = false;

  setSyncing(value: boolean) {
    this.syncing = value;
  }
}
const syncQueueMiddleware = (store) => {
  const syncQueue = new SyncQueue();
  const syncState = new SyncState();

  const updateBoardItemsOrder = async () => {
    if (!syncQueue.shouldUpdateItemsOrder) {
      return;
    }

    const orders = syncQueue.getItemsOrderQueue();
    syncQueue.clearItemsOrderQueue();
    try {
      const workspaceId = selectCurrentWorkspaceId(store.getState() as AppState);

      const promises = await Promise.allSettled(
        orders.map(([uuid, col]) =>
          updateItemsOrder({
            columnUuid: uuid,
            items: col.order,
            updatedAt: col.updatedAt,
            workspaceId,
          })
        )
      );

      const toRetry = promises.reduce((acc, promise, index) => {
        if (promise.status === 'rejected') {
          const order = orders[index];

          return [...acc, order];
        }
        return acc;
      }, []);

      if (toRetry.length > 0) {
        throw { toRetry };
      }
    } catch (error) {
      const { toRetry } = error;

      toRetry.forEach(([colUuid, columnn]) => {
        syncQueue.addItemsOrderQueue(colUuid, columnn.order, columnn.updatedAt);
      });

      throw 'An error occurred during the board items ordering updating';
    }
  };

  const updateColumnsOrdering = async () => {
    if (!syncQueue.shouldUpdateColumnsOrder) {
      return;
    }

    const { order, updatedAt } = syncQueue.getColumnsOrderQueue();

    syncQueue.clearColumnsOrderQueue();

    try {
      const workspaceId = selectCurrentWorkspaceId(store.getState() as AppState);

      await updateTagsColumnsOrdering({ order, updatedAt, workspaceId });
    } catch {
      if (!syncQueue.shouldUpdateColumnsOrder) {
        syncQueue.setColumnsOrderQueue(order, updatedAt);
      }

      throw 'An error occurred during the board columns order updating';
    }
  };

  const deleteColumns = async () => {
    if (!syncQueue.shouldDeleteColumns) {
      return;
    }

    const uuids = syncQueue.getColumnsToDeleteQueue();
    syncQueue.clearColumnsToDeleteQueue();

    try {
      const workspaceId = selectCurrentWorkspaceId(store.getState() as AppState);

      await deleteTagsColumns(uuids, workspaceId);
    } catch {
      syncQueue.addColumnsToDeleteQueue(uuids);

      throw 'An error occurred during the board columns deleting';
    }
  };

  const deleteBoardItems = async () => {
    if (!syncQueue.shouldDeleteItems) {
      return;
    }

    const itemsToDelete = syncQueue.getItemsToDeleteQueue();
    syncQueue.clearItemsToDeleteQueue();

    try {
      const workspaceId = selectCurrentWorkspaceId(store.getState() as AppState);

      await deleteItems(itemsToDelete, workspaceId);
    } catch {
      syncQueue.addItemsToDeleteQueue(itemsToDelete);

      throw 'An error occurred during the board items deleting';
    }
  };

  const updateBoardItems = async () => {
    if (!syncQueue.shouldUpdateItems) {
      return;
    }

    const items = syncQueue.getItemsToUpdateQueue();
    syncQueue.clearItemsToUpdateQueue();

    try {
      const workspaceId = selectCurrentWorkspaceId(store.getState() as AppState);

      const { failed } = await updateItems(items, workspaceId);

      if (failed.length > 0) {
        throw { toRetry: items.filter(({ id }) => failed.includes(id)) };
      }
    } catch (error) {
      const itemsToRetry = (error.toRetry as TagItem[]) || items;
      syncQueue.addItemsToUpdateQueue(itemsToRetry);

      throw 'An error occurred during the board items updating';
    }
  };
  const syncBoard = async () => {
    if (syncState.syncing) {
      return;
    }

    syncState.setSyncing(true);

    try {
      await deleteColumns();
      await updateColumnsOrdering();
      await deleteBoardItems();
      await updateBoardItems();
      await updateBoardItemsOrder();

      syncState.setSyncing(false);
      store.dispatch(tagsBoardActions.setShouldSync(false));

      if (syncQueue.shouldSync) {
        syncBoard();
      }
    } catch (error) {
      syncState.setSyncing(false);
      store.dispatch(tagsBoardActions.setShouldSync(false));

      store.dispatch(tagsBoardActions.setSyncFailed(true));
      syncQueue.clearQueue();

      Sentry.captureException(error);
    }
  };

  const debouncedSyncBoard = debounce(syncBoard, 2000);

  const actionTypesToSync = {
    [tagsBoardActions.moveColumn.type]: (boardState: TagBoard) => {
      syncQueue.setColumnsOrderQueue(
        boardState.columns.map(({ id }) => id),
        Math.floor(Date.now() / 1000)
      );
    },
    [tagsBoardActions.removeTag.type]: (_, action) => {
      const uuid = action.payload as string;

      syncQueue.addColumnsToDeleteQueue([uuid]);
    },
    [tagsBoardActions.deleteItems.type]: (_, action) => {
      const { itemsUuids } = action.payload;

      syncQueue.addItemsToDeleteQueue(itemsUuids);
    },
    [tagsBoardActions.addItem.type]: (_: TagBoard, action) => {
      const { item } = action.payload;
      syncQueue.addItemsToUpdateQueue([item]);
    },
    [tagsBoardActions.moveItem.type]: (boardState: TagBoard, action) => {
      const { itemId, destColumnId } = action.payload;
      const item = boardState.columns
        .find((colItem) => colItem.id === destColumnId)
        .items.find(({ id }) => id === itemId);
      syncQueue.addItemsToUpdateQueue([{ ...item }]);
    },
    [tagsBoardActions.changeItemOrder.type]: (boardState: TagBoard, action) => {
      const { columnId } = action.payload;
      syncQueue.addItemsOrderQueue(
        action.payload.columnId,
        boardState.columns.find((colItem) => colItem.id === columnId).items,
        Math.floor(Date.now() / 1000)
      );
    },
  };

  return (next) => (action) => {
    if (actionTypesToSync[action.type]) {
      const result = next(action);

      const state = store.getState() as AppState;
      const boardState = selectTagsBoard(state);

      const addChangesToQueue = actionTypesToSync[action.type];

      addChangesToQueue(boardState, action);
      store.dispatch(tagsBoardActions.setShouldSync(true));
      debouncedSyncBoard();

      return result;
    } else {
      return next(action);
    }
  };
};

export { syncQueueMiddleware };
